import { useEffect, useRef } from 'react';
import { Outlet } from 'react-router';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppSelector } from '~/hooks/useAppSelector';
import {
  type IShiftDetailsFormValues,
  type IShiftOverride,
} from './ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { OfficeMissingInfoGate } from './OfficeMissingInfo/OfficeMissingInfoGate';
import { ShiftRequestFlowTrackingProvider } from './ShiftRequestFlowTracking';

export const ShiftRequestLayout = () => {
  const userAssistedHygiene = useAppSelector(
    (state) => state.user.selectedChildOffice?.office_info?.isAssistedHygiene,
  );

  const methods = useForm<IShiftDetailsFormValues>({
    defaultValues: {
      dates: [],
      professionType: '',
      officeId: '',
      arrivalTime: '08:00 am',
      departureTime: '05:00 pm',
      // Default the meal break to 60 min / unpaid — ~90% of shifts use these,
      // saving most users a step.
      mealBreakDuration: '1 hr',
      isMealBreakPaid: false,
      rate: null,
      recommendedRate: null,
      preferredSpecialty: '',
      isAssistedHygiene: userAssistedHygiene ?? undefined,
      procedures: [],
      invite: [],
      inviteNames: {},
      inviteSource: 'Shift Request',
      autoConfirm: { favorites: false, eligibleProfessionals: false },
      shiftOverrides: {},
    },
  });

  // Reset dependent fields when professionType changes
  const previousProfessionTypeRef = useRef(methods.getValues('professionType'));
  // Reset the rate recommendation when the office changes
  const previousOfficeIdRef = useRef(methods.getValues('officeId'));

  useEffect(() => {
    const subscription = methods.watch((values, { name }) => {
      if (
        name === 'professionType' &&
        values.professionType !== previousProfessionTypeRef.current
      ) {
        previousProfessionTypeRef.current = values.professionType || '';
        methods.setValue('rate', null);
        methods.setValue('recommendedRate', null);
        methods.setValue('preferredSpecialty', '');
        methods.setValue('procedures', []);
        methods.setValue('autoConfirm', { favorites: false, eligibleProfessionals: false });
      }

      // Reset the office-dependent fields when the office changes: the rate so the
      // recommendation reflects the newly-selected office's state average instead of
      // the previous office's stale value, and auto-confirm because it requires a
      // payable office — carrying it over gets the whole request rejected when the
      // new office has no payment method, with the toggle locked out of reach.
      if (name === 'officeId' && values.officeId !== previousOfficeIdRef.current) {
        previousOfficeIdRef.current = values.officeId || '';
        methods.setValue('rate', null);
        methods.setValue('recommendedRate', null);
        methods.setValue('autoConfirm', { favorites: false, eligibleProfessionals: false });

        // Per-day overrides win over the form-level values when the payload is built,
        // so clearing only the form-level ones would still submit the previous office's
        // auto-confirm and rate. Drop both to `undefined` rather than to an explicit
        // "off": the payload resolves an override with `??`, so only a nullish value
        // makes the day fall back to the form. An explicit `false` would keep winning
        // and silently post that one day without auto-confirm while the section shows
        // it on. The rest of each override stays — time, specialty and procedures are
        // not office-dependent.
        const overrides = methods.getValues('shiftOverrides') ?? {};
        const dateKeys = Object.keys(overrides);

        if (dateKeys.length) {
          methods.setValue(
            'shiftOverrides',
            dateKeys.reduce<Record<string, IShiftOverride>>((reset, dateKey) => {
              reset[dateKey] = {
                ...overrides[dateKey],
                rate: null,
                autoConfirm: undefined,
              };

              return reset;
            }, {}),
          );
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  return (
    <FormProvider {...methods}>
      <ShiftRequestFlowTrackingProvider>
        <Outlet />
      </ShiftRequestFlowTrackingProvider>
      <OfficeMissingInfoGate />
    </FormProvider>
  );
};
